/*
 *   File : courses-created.js
 *   Author : https://evoqins.com
 *   Description : Courses created component
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { Fragment, useEffect, useRef, useState } from 'react';

//Custom Component
import { DataTableComponent } from '../../../Component/DataTable';
import { CustomSelectBox } from '../../../Component/FormElements';
import { SummaryCard } from '../../../Component/Cards';
import { EmptyScreen } from '../../../Component/EmptyComponent';
import { Loader } from '../../../Component/Others';

// Service
import { APIService } from '../../../Service';

const ITEMS_PER_PAGE = 15;

const PAGE_SORT_FIELDS = [
    'course_id',
    'course_name',
    'category',
    'status',
    'points',
];

const CATEGORY_OPTIONS = [
    {
        value: null,
        label: 'All',
    },
    {
        value: 1,
        label: 'Product'
    },
    {
        value: 2,
        label: 'Skills and service'
    },
];


const STATUS_OPTIONS = [
    {
        value: null,
        label: 'All',
    },
    {
        value: 1,
        label: 'Active'
    },
    {
        value: 2,
        label: 'Archived'
    },
];


const CoursesCreated = React.memo((props) => {

    const tableRef = useRef(null);
    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    const [toggleFilter, setToggleFilter] = useState(false);

    const [apiLoader, setApiLoader] = useState(true);

    const [selectedCategory, setSelectedCategory] = useState(CATEGORY_OPTIONS[0]);
    const [selectedStatus, setSelectedStatus] = useState(STATUS_OPTIONS[0]);
    const [pageNumber, setPageNumber] = useState(1);

    const [courseData, setCourseData] = useState({});
    const [summaryData, setSummaryData] = useState({});

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    useEffect(() => {
        if (skipListFetchRef.current) {
            skipListFetchRef.current = false;
            return;
        }
        _getCourses();
        //eslint-disable-next-line
    }, [pageNumber, selectedCategory, selectedStatus]);

    // handle page change 
    const _handlePageChange = (page_num) => {
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === pageNumber) {
            return;
        }

        setPageNumber(page_num);
        setApiLoader(true);

        if (tableRef.current) {
            tableRef.current.scrollIntoView({ behavior: 'smooth', block: "start" });
        }
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !PAGE_SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setApiLoader(true);

        // Only skip the list useEffect when page will actually change.
        if (pageNumber !== 1) {
            skipListFetchRef.current = true;
        }
        setPageNumber(1);
        _getCourses(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    const _handleCategoryChange = (temp) => {
        setSelectedCategory(temp);
        setSortField(null);
        setSortDirection(null);
        setApiLoader(true);
        skipListFetchRef.current = true;
        setPageNumber(1);
        _getCourses(1, null, null, temp.value, selectedStatus.value);
    };

    const _handleStatusChange = (temp) => {
        setSelectedStatus(temp);
        setSortField(null);
        setSortDirection(null);
        setApiLoader(true);
        skipListFetchRef.current = true;
        setPageNumber(1);
        _getCourses(1, null, null, selectedCategory.value, temp.value);
    };

    const TABLE_COLUMNS = [
        {
            name: 'Video name',
            id: 'course_name',
            sortField: 'course_name',
            selector: row => row.course_name,
            width: "320px",
            sortable: true,
        },
        {
            name: 'Category',
            id: 'category',
            sortField: 'category',
            selector: row => row.category,
            sortable: true,
        },
        {
            name: 'Linked products',
            cell: row => row.linked_products.length > 0 && row.linked_products ? row.linked_products.map((item, key) => `${item.product} ${key === row.linked_products.length - 1 ? "" : " , "}`) : "-",
        },
        {
            name: 'Status',
            id: 'status',
            sortField: 'status',
            // selector: row => row.status,
            cell: row =>
                <>
                    {row.status ?
                        <div className={`${row.status === "Active" ? `e-bg-tea-green px-3` :
                            row.status === "Archived" ? `e-bg-light-red padding-12px-lr` : ``}  e-border-radius-16 e-font-weight-500 e-font-12 padding-2px-tb`}>
                            {row.status}
                        </div>
                        :
                        "-"
                    }
                </>,
            sortable: true,
        },
        {
            name: 'Views',
            selector: row => row.enrolled_users
        },
        {
            name: "Reward points",
            id: 'points',
            sortField: 'points',
            selector: row => row.points,
            sortable: true,
        }

    ];

    // API - course list
    const _getCourses = (
        page_num = pageNumber,
        sort_by = sortField,
        sort_order = sortDirection,
        category_filter = selectedCategory.value,
        status_filter = selectedStatus.value
    ) => {
        const effective_sort_by = sort_by && PAGE_SORT_FIELDS.includes(sort_by) ? sort_by : null;
        const effective_sort_order = effective_sort_by ? sort_order : null;

        const url = "country-admin/course";

        const request = {
            "admin_id": props.adminId,
            "page_num": page_num,
            "limit": ITEMS_PER_PAGE,
            "category_filter": category_filter,
            "status_filter": status_filter,
            "sort_by": effective_sort_by ? [effective_sort_by] : null,
            "order": effective_sort_order ? [effective_sort_order] : null,
        }

        APIService(true, url, request).then((response) => {
            if (response.status_code === 200) {
                setCourseData(response.data);
                setSummaryData(response.data.summary);
            } else {
                setSummaryData({});
                setCourseData({});
            }
            setApiLoader(false);
        });
    }

    return (
        <>
            {apiLoader ?
                <div className='margin-20px-top e-bg-white e-admin-box-shadow py-4 e-border-radius-16 '>
                    {/* loader */}
                    <Loader />
                </div>
                :
                <Fragment>
                    {/* Summary  */}
                    <div className='e-admin-box-shadow p-4 e-bg-white e-border-radius-16'>
                        <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-500 line-height-28px'>
                            Summary
                        </h4>
                        <div className='row row-gap-3 row-cols-xl-5 row-cols-lg-4 row-cols-md-4 row-cols-sm-3 row-cols-2 padding-10px-top'>
                            <div className='col'>
                                <SummaryCard title="Courses created"
                                    quantity={summaryData.courses_created} />
                            </div>
                            <div className='col'>
                                <SummaryCard title="Active courses"
                                    quantity={summaryData.active_courses} />
                            </div>
                            <div className='col'>
                                <SummaryCard title="Archived courses"
                                    quantity={summaryData.archived_courses} />
                            </div>
                            <div className='col'>
                                <SummaryCard title="Average enrollments"
                                    quantity={summaryData.avg_enrollments} />
                            </div>
                        </div>
                    </div>

                    {/* Courses created table */}
                    <div ref={tableRef} className='margin-20px-top e-bg-white e-admin-box-shadow py-4 e-border-radius-16 '>
                        <div className='d-flex px-4 justify-content-space-between'>
                            <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-20 e-font-weight-600 line-height-28px'>
                                Courses created
                            </h4>
                            <img src={toggleFilter ? require("../../../Assets/Image/filter-icon-white.svg").default : require("../../../Assets/Image/filter-icon.svg").default}
                                draggable={false}
                                className={`${toggleFilter ? `e-bg-indigo` : ``} transition e-border-1px-anti-flash-white  e-border-radius-8 p-2 cursor-pointer`}
                                alt='filter'
                                onClick={() => setToggleFilter(!toggleFilter)} />
                        </div>

                        {/* filter */}
                        {toggleFilter &&
                            <div className='e-bg-anti-flash-white-dark py-3 px-4 mt-2'>
                                <div className='row'>
                                    <div className='col-xl-2 col-lg-3 col-md-3 col-sm-4 col-6'>
                                        <CustomSelectBox label="Category"
                                            value={selectedCategory}
                                            options={CATEGORY_OPTIONS}
                                            className="e-bg-white e-border-radius-8 e-table-select"
                                            isSearchable={false}
                                            selectChange={_handleCategoryChange}
                                        />
                                    </div>
                                    <div className='col-xl-2 col-lg-3 col-md-3 col-sm-4 col-6'>
                                        <CustomSelectBox label="Status"
                                            value={selectedStatus}
                                            options={STATUS_OPTIONS}
                                            className="e-bg-white e-border-radius-8 e-table-select"
                                            isSearchable={false}
                                            selectChange={_handleStatusChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        }


                        {/* Data table */}
                        {
                            courseData.courses.length !== 0 ?
                                <div className='e-table'>
                                    <DataTableComponent
                                        key={`courses-created-${pageNumber}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                        columns={TABLE_COLUMNS}
                                        pagination={true}
                                        paginationServer={true}
                                        paginationPerPage={ITEMS_PER_PAGE}
                                        paginationTotalRows={courseData.total_count}
                                        paginationDefaultPage={pageNumber}
                                        paginationResetDefaultPage={false}
                                        sortServer={true}
                                        defaultSortFieldId={sortField}
                                        defaultSortAsc={sortDirection !== 'desc'}
                                        onSort={_handleSort}
                                        onChangePage={_handlePageChange}
                                        data={courseData.courses} />
                                </div>
                                :
                                <div className='h-100 py-5 my-5'>
                                    <EmptyScreen message="No courses found" />
                                </div>
                        }
                    </div>
                </Fragment>
            }
        </>
    )
})

export default CoursesCreated;
